import { LoadingButton } from '@mui/lab';
import { Alert, Box, IconButton, Typography } from '@mui/material';
import {
  DocumentEditorContainerComponent,
  Toolbar,
} from '@syncfusion/ej2-react-documenteditor';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { ErrorMessage } from 'src/shared/components/ErrorMessage/ErrorMessage';
import { FullscreenLoadingIndicator } from 'src/shared/components/FullscreenLoadingIndicator/FullscreenLoadingIndicator';
import { Icon } from 'src/shared/components/Icon/Icon';

DocumentEditorContainerComponent.Inject(Toolbar);

const SERVICE_URL =
  'https://ej2services.syncfusion.com/production/web-services/api/documenteditor/';

const SFDT_CONVERT_URI = '/core/document-to-sfdt/';

const localeMap: Record<string, string> = {
  en: 'en-US',
  'sv-SE': 'sv-SE',
};

type ErrorDetails = {
  hideEditor: boolean;
  message?: string;
  originalError?: Error;
  title: string;
};

type SyncfusionEditorProps = {
  extension: string;
  filename: string;
  onSaveChanges: (file: File) => Promise<void>;
  url: string;
};

export const SyncfusionEditor = ({
  extension,
  filename,
  onSaveChanges,
  url,
}: SyncfusionEditorProps) => {
  const { i18n, t } = useTranslation('vault');
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  const documentEditorContainerRef =
    useRef<DocumentEditorContainerComponent>(null);

  const [isLoadingDocument, setIsLoadingDocument] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isChanged, setIsChanged] = useState(false);
  const [error, setError] = useState<ErrorDetails>();

  const locale = localeMap[i18n.language] || 'en-US';
  const height = 'calc(100vh - 200px)';

  useLayoutEffect(() => {
    const updateSize = () => {
      documentEditorContainerRef.current?.documentEditor.resize();
    };
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    (async () => {
      setIsLoadingDocument(true);
      setError(undefined);

      try {
        const response = await fetch(url, { cache: 'no-store' });

        if (response.ok) {
          const blob = await response.blob();
          const form = new FormData();
          form.append('file', new File([blob], filename, { type: extension }));
          const sfdtResponse = await axios.post(SFDT_CONVERT_URI, form, {
            params: { secure: true },
          });
          documentEditorContainerRef.current?.documentEditor.open(
            sfdtResponse.data
          );
        } else {
          const detail = await response.text();
          setError({
            hideEditor: true,
            message: detail,
            title: t('FAILED_TO_LOAD_DOCUMENT'),
          });
        }
      } catch (error) {
        setError({
          hideEditor: true,
          message: error?.message,
          originalError: error,
          title: t('FAILED_TO_LOAD_DOCUMENT'),
        });
      } finally {
        setIsLoadingDocument(false);
      }
    })();
  }, [documentEditorContainerRef, extension, filename, t, url]);

  const onClickSave = useCallback(async () => {
    if (!documentEditorContainerRef.current) return;

    setIsSaving(true);
    setError(undefined);

    try {
      const blob =
        await documentEditorContainerRef.current.documentEditor.saveAsBlob(
          'Docx'
        );
      await onSaveChanges(new File([blob], filename, { type: extension }));
      enqueueSnackbar(t('SAVED_SUCCESSFULLY'), { variant: 'success' });
      setIsChanged(false);
    } catch (error) {
      enqueueSnackbar(t('FAILED_TO_SAVE'), { variant: 'error' });
      setError({
        hideEditor: false,
        message: error?.message,
        originalError: error,
        title: t('FAILED_TO_SAVE'),
      });
    } finally {
      setIsSaving(false);
    }
  }, [enqueueSnackbar, extension, filename, onSaveChanges, t]);

  return (
    <Box>
      {isLoadingDocument && <FullscreenLoadingIndicator />}

      <Box sx={{ opacity: isLoadingDocument ? 0 : 1 }}>
        <Box
          alignItems="center"
          display="flex"
          justifyContent="space-between"
          mb={2}
        >
          <Box alignItems="center" display="flex">
            <IconButton onClick={() => navigate(-1)} sx={{ mr: 1 }}>
              <Icon icon="eva:arrow-back-fill" />
            </IconButton>
            <Typography variant="h5">{filename}</Typography>

            {isChanged ? (
              <Alert
                icon={false}
                severity="warning"
                sx={{
                  '.MuiAlert-message': { p: 0.5 },
                  fontSize: 12,
                  ml: 1,
                  p: 0.5,
                }}
              >
                {t('CHANGES_NOT_SAVED')}
              </Alert>
            ) : null}
          </Box>

          {!error?.hideEditor ? (
            <LoadingButton
              disabled={isSaving || !isChanged}
              loading={isSaving}
              loadingPosition="start"
              onClick={onClickSave}
              startIcon={<Icon icon="eva:save-fill" />}
              variant="contained"
            >
              {t('SAVE_CHANGES')}
            </LoadingButton>
          ) : null}
        </Box>

        {error ? (
          <ErrorMessage
            error={error.originalError}
            message={error.message}
            sx={{ mb: error.hideEditor ? 0 : 2 }}
            title={error.title}
          />
        ) : null}

        {!error?.hideEditor ? (
          <DocumentEditorContainerComponent
            contentChange={() => setIsChanged(true)}
            enableToolbar
            height={height}
            locale={locale}
            ref={documentEditorContainerRef}
            serviceUrl={SERVICE_URL}
          />
        ) : null}
      </Box>
    </Box>
  );
};
